import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import {
  useGetEnrollmentHYCSDocumentVerificationCompleteData,
  useSubmitEnrollmentHYCSDocumentVerificationCompleteData,
} from '@egw/api'
import { Button, Alert, AlertType, GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { sendEvent } from '@egw/events'
import { ROUTES } from '../../../constants'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'

const log = new Logger('Enrollment - HYCS - Document Verification Complete')

export const DocumentVerificationCompletePage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetEnrollmentHYCSDocumentVerificationCompleteData()
  const {
    mutate,
    isLoading: isSubmitting,
    error: submitError,
  } = useSubmitEnrollmentHYCSDocumentVerificationCompleteData()

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = async (): Promise<void> => {
    try {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_DOCV_SUBMIT)

      const response = await mutate({
        proceedDocumentVerificationComplete: true,
      })
      if (response?.path) {
        void navigate(response.path)
      } else {
        // TODO: Handle the case when path is not returned
        // void navigate(ROUTES.HOME.path)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  return (
    <EnrollmentHYCSLayout title={t('hycs.verification-complete.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <div className="w-full flex-1 flex flex-col justify-start lg:justify-center">
            <PageHeader title={t('hycs.verification-complete.title')} className="mb-md" />
            <p className="typo-primary-base-regular text-text-meta">{t('hycs.verification-complete.description')}</p>

            <div className="flex-1 md:hidden" />

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isLoading={isSubmitting}
              testId="primaryAction_button"
              onClick={() => void onSubmit()}
            >
              {t('common.continue')}
            </Button>
          </div>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
